import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  HardDrive, 
  FolderOpen, 
  File, 
  Upload, 
  Trash2, 
  RefreshCw, 
  Link2, 
  Link2Off,
  ChevronRight,
  Home,
  Image,
  FileText,
  Loader2,
  CheckCircle,
  ExternalLink,
  ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useAuth } from '../App';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StoragePage = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [driveStatus, setDriveStatus] = useState({ connected: false, loading: true });
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    checkDriveStatus();
    
    // Check for connection success/error from OAuth callback
    if (searchParams.get('connected') === 'true') {
      toast.success('Google Drive connected successfully!');
      navigate('/admin/storage', { replace: true });
    }
    if (searchParams.get('error')) {
      toast.error(`Connection failed: ${searchParams.get('error')}`);
      navigate('/admin/storage', { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (driveStatus.connected) {
      loadFolderContents(currentPath);
    }
  }, [driveStatus.connected, currentPath]);

  const checkDriveStatus = async () => {
    try {
      const response = await axios.get(`${API}/drive/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDriveStatus({ ...response.data, loading: false });
    } catch (err) {
      setDriveStatus({ connected: false, loading: false });
    }
  };

  const connectDrive = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API}/drive/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (err) {
      toast.error('Failed to initiate connection');
      setConnecting(false);
    }
  };

  const disconnectDrive = async () => {
    if (!window.confirm('Disconnect Google Drive? Your files will remain in Drive but won\'t be accessible from this app.')) return;
    
    try {
      await axios.delete(`${API}/drive/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDriveStatus({ connected: false, loading: false });
      setItems([]);
      setCurrentPath('');
      toast.success('Google Drive disconnected');
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  const loadFolderContents = async (path) => {
    setLoadingItems(true);
    try {
      const response = await axios.get(`${API}/drive/folders`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { path }
      });
      setItems(response.data.items || []);
    } catch (err) {
      toast.error('Failed to load folder contents');
      setItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const navigateToFolder = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
  };

  const navigateUp = () => {
    const parts = currentPath.split('/');
    parts.pop();
    setCurrentPath(parts.join('/'));
  };

  const navigateToRoot = () => {
    setCurrentPath('');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Determine upload path - default to current year/receipts if no path
    let uploadPath = currentPath;
    if (!uploadPath.includes('SPT Digital Marketing')) {
      uploadPath = `${new Date().getFullYear()}/receipts`;
    } else {
      // Remove "SPT Digital Marketing/" prefix for API
      uploadPath = uploadPath.replace('SPT Digital Marketing/', '');
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/drive/upload?folder_path=${encodeURIComponent(uploadPath)}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('File uploaded successfully!');
      loadFolderContents(currentPath);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const deleteFile = async (fileId, fileName) => {
    if (!window.confirm(`Delete "${fileName}"?`)) return;

    try {
      await axios.delete(`${API}/drive/file/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('File deleted');
      loadFolderContents(currentPath);
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const getFileIcon = (item) => {
    if (item.is_folder) return <FolderOpen className="w-5 h-5 text-amber-500" />;
    if (item.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)) return <Image className="w-5 h-5 text-green-500" />;
    if (item.name.match(/\.(pdf|doc|docx)$/i)) return <FileText className="w-5 h-5 text-red-500" />;
    return <File className="w-5 h-5 text-slate-400" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const pathParts = currentPath ? currentPath.split('/') : [];

  if (driveStatus.loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F3C4F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">File Storage</h1>
          <p className="text-slate-500">Manage your business files with Google Drive</p>
        </div>
        {driveStatus.connected && (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full">
              <CheckCircle className="w-4 h-4" />
              Connected
            </span>
            <Button variant="outline" size="sm" onClick={disconnectDrive}>
              <Link2Off className="w-4 h-4 mr-1" />
              Disconnect
            </Button>
          </div>
        )}
      </div>

      {!driveStatus.connected ? (
        /* Not Connected State */
        <Card className="border-0 shadow-lg">
          <CardContent className="py-16 text-center">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-500 to-green-500 rounded-2xl flex items-center justify-center mb-6">
              <HardDrive className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-3">
              Connect Google Drive
            </h2>
            <p className="text-slate-500 max-w-md mx-auto mb-8">
              Store your invoices, receipts, and business files in organized folders on your Google Drive. 
              Access them anywhere, anytime.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
              <div className="p-4 bg-slate-50 rounded-lg">
                <FolderOpen className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Organized Folders</p>
                <p className="text-xs text-slate-500">2026/invoices, receipts, jobs</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <Upload className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Auto-Upload</p>
                <p className="text-xs text-slate-500">Receipts saved automatically</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <ExternalLink className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Access Anywhere</p>
                <p className="text-xs text-slate-500">View files on any device</p>
              </div>
            </div>

            <Button 
              onClick={connectDrive} 
              disabled={connecting}
              className="bg-[#1F3C4F] hover:bg-[#152a38]"
              size="lg"
            >
              {connecting ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Link2 className="w-5 h-5 mr-2" />
              )}
              Connect Google Drive
            </Button>
          </CardContent>
        </Card>
      ) : (
        /* Connected State - File Browser */
        <>
          {/* Toolbar */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                {/* Breadcrumb */}
                <div className="flex items-center gap-1 text-sm">
                  <button 
                    onClick={navigateToRoot}
                    className="flex items-center gap-1 hover:text-[#1F3C4F] text-slate-500"
                  >
                    <Home className="w-4 h-4" />
                    <span>Drive</span>
                  </button>
                  {pathParts.map((part, index) => (
                    <React.Fragment key={index}>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                      <button
                        onClick={() => {
                          const newPath = pathParts.slice(0, index + 1).join('/');
                          setCurrentPath(newPath);
                        }}
                        className={`hover:text-[#1F3C4F] ${
                          index === pathParts.length - 1 ? 'font-medium text-slate-900' : 'text-slate-500'
                        }`}
                      >
                        {part}
                      </button>
                    </React.Fragment>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadFolderContents(currentPath)}
                    disabled={loadingItems}
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingItems ? 'animate-spin' : ''}`} />
                  </Button>
                  <label>
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                    <Button
                      variant="default"
                      size="sm"
                      className="bg-[#1F3C4F] hover:bg-[#152a38] cursor-pointer"
                      asChild
                    >
                      <span>
                        {uploading ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-1" />
                        )}
                        Upload
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* File List */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {loadingItems ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-[#1F3C4F]" />
                </div>
              ) : items.length === 0 ? (
                <div className="py-16 text-center">
                  <FolderOpen className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">This folder is empty</p>
                  {currentPath && (
                    <Button variant="link" onClick={navigateUp} className="mt-2">
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Go back
                    </Button>
                  )}
                </div>
              ) : (
                <div className="divide-y">
                  {/* Go up button */}
                  {currentPath && (
                    <button
                      onClick={navigateUp}
                      className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors text-left"
                    >
                      <ArrowLeft className="w-5 h-5 text-slate-400" />
                      <span className="text-slate-500">..</span>
                    </button>
                  )}
                  
                  {/* Items */}
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 hover:bg-slate-50 transition-colors"
                    >
                      <button
                        onClick={() => item.is_folder && navigateToFolder(item.name)}
                        className={`flex items-center gap-3 flex-1 text-left ${
                          item.is_folder ? 'cursor-pointer' : 'cursor-default'
                        }`}
                        disabled={!item.is_folder}
                      >
                        {getFileIcon(item)}
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900 truncate">{item.name}</p>
                          <p className="text-xs text-slate-500">
                            {item.is_folder ? 'Folder' : formatFileSize(item.size)}
                            {item.modified && ` • ${new Date(item.modified).toLocaleDateString()}`}
                          </p>
                        </div>
                      </button>
                      
                      <div className="flex items-center gap-1">
                        {item.web_url && (
                          <a
                            href={item.web_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-slate-400 hover:text-[#1F3C4F] transition-colors"
                            title="Open in Google Drive"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        {!item.is_folder && (
                          <button
                            onClick={() => deleteFile(item.id, item.name)}
                            className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Links */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card 
              className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setCurrentPath(`SPT Digital Marketing/${new Date().getFullYear()}/invoices`)}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">Invoices</p>
                  <p className="text-xs text-slate-500">{new Date().getFullYear()}/invoices</p>
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setCurrentPath(`SPT Digital Marketing/${new Date().getFullYear()}/receipts`)}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Image className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Receipts</p>
                  <p className="text-xs text-slate-500">{new Date().getFullYear()}/receipts</p>
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setCurrentPath(`SPT Digital Marketing/${new Date().getFullYear()}/jobs`)}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FolderOpen className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium">Jobs</p>
                  <p className="text-xs text-slate-500">{new Date().getFullYear()}/jobs</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default StoragePage;
